import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { Loader2, DollarSign, Clock, Plug } from 'lucide-react'
import {
  installPreBuiltTask,
  selectPreBuiltInstallStatus,
} from '../../../features/openai/openaiSlice'
import { selectRootUser } from '../../../rootSlice'
import { getSecretId } from '../../Integrations/channels/generic/helpers'
import { getTeamMembers } from 'actions/teamMembersActions'
import { detectBrowserTimezone } from '../../../modules/timezone'
import TaskScheduleFields from '../../AI/components/TaskScheduleFields'
import TaskDeliveryFields from '../../AI/components/TaskDeliveryFields'
import { buildSchedulePreview } from '../../AI/utils/schedulePreview'

interface Props {
  task: any
  open: boolean
  onClose: () => void
}

function formatSavings(cents: number | null | undefined): string {
  if (!cents || cents <= 0) return '—'
  const dollars = cents / 100
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, '')}K/yr`
  }
  return `$${dollars.toFixed(0)}/yr`
}

function formatRunTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—'
  if (seconds < 90) return `~${seconds}s`
  const m = Math.round(seconds / 60)
  return `~${m}m`
}

function formatIntegrationLabel(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b([a-z])/g, (m) => m.toUpperCase())
}

function initialNotifyEmails(task: any, userEmail: string): string[] {
  const cfg = task?.delivery_config || {}
  if (Array.isArray(cfg.notify_emails) && cfg.notify_emails.length > 0) {
    return [
      ...new Set(
        cfg.notify_emails.map((e: unknown) => String(e).trim().toLowerCase()).filter(Boolean),
      ),
    ]
  }
  if (cfg.recipient_email) return [String(cfg.recipient_email).trim().toLowerCase()]
  if (userEmail) return [String(userEmail).trim().toLowerCase()]
  return []
}

export default function InstallDialog({ task, open, onClose }: Props) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectRootUser) as any
  const installStatus = useSelector(selectPreBuiltInstallStatus)
  const teamMembers = useSelector((store: any) => store.teamMembers?.members) as
    | Array<{ shared_with?: string; email?: string }>
    | undefined
  const teamMembersStatus = useSelector((store: any) => store.teamMembers?.fetching) as boolean | undefined
  const userEmail = user?.email || ''

  const [scheduleType, setScheduleType] = useState<string>((task?.schedule_type as any) || 'weekly')
  const initialCfg = task?.schedule_config || {}
  const [time, setTime] = useState<string>(initialCfg.time || '08:00')
  const [timezone, setTimezone] = useState<string>(initialCfg.timezone || detectBrowserTimezone())
  const [dayOfWeek, setDayOfWeek] = useState<number>(
    initialCfg.day_of_week != null ? Number(initialCfg.day_of_week) : 1,
  )
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    initialCfg.day_of_month != null ? Number(initialCfg.day_of_month) : 1,
  )
  const [notifyOnComplete, setNotifyOnComplete] = useState<boolean>(
    !!(
      task?.delivery_config?.notify_on_complete ||
      task?.delivery_config?.recipient_email ||
      (Array.isArray(task?.delivery_config?.notify_emails) &&
        task.delivery_config.notify_emails.length > 0)
    ),
  )
  const [selectedNotifyEmails, setSelectedNotifyEmails] = useState<string[]>(() =>
    initialNotifyEmails(task, userEmail),
  )

  useEffect(() => {
    if (open) dispatch(getTeamMembers() as any)
  }, [open, dispatch])

  const toggleNotifyEmail = useCallback((email: string) => {
    const normalized = String(email || '').trim().toLowerCase()
    if (!normalized) return
    setSelectedNotifyEmails((prev) =>
      prev.includes(normalized) ? prev.filter((e) => e !== normalized) : [...prev, normalized],
    )
  }, [])

  const previewLine = useMemo(
    () =>
      buildSchedulePreview({
        schedule_type: scheduleType,
        time,
        timezone,
        day_of_week: dayOfWeek,
        day_of_month: dayOfMonth,
      }),
    [scheduleType, time, timezone, dayOfWeek, dayOfMonth],
  )

  if (!open) return null

  const installing = installStatus === 'installing'

  const handleInstall = async () => {
    if (!user?.id) {
      toast.error('You must be signed in to install a task.')
      return
    }
    const ownerId = user.ownerId || user.id
    const key_id = getSecretId(ownerId, 'payhelm_ai_mcp')
    const delivery_config: Record<string, any> = {
      ...(task?.delivery_config || {}),
    }
    delete delivery_config.recipient_email
    if (notifyOnComplete) {
      const emails =
        selectedNotifyEmails.length > 0
          ? selectedNotifyEmails
          : userEmail
            ? [String(userEmail).trim().toLowerCase()]
            : []
      delivery_config.notify_on_complete = true
      if (emails.length > 0) delivery_config.notify_emails = emails
      else delete delivery_config.notify_emails
    } else {
      delivery_config.notify_on_complete = false
      delete delivery_config.notify_emails
    }
    try {
      const resp: any = await (
        dispatch(
          installPreBuiltTask({
            slug: task.slug,
            key_id,
            schedule_type: scheduleType,
            time,
            timezone,
            day_of_week: dayOfWeek,
            day_of_month: dayOfMonth,
            delivery_config,
          }) as any,
        ) as any
      ).unwrap()
      toast.success(`Added "${task.name}" to your tasks`)
      onClose()
      const newTaskId = resp?.scheduled_task?.id
      if (newTaskId) {
        navigate(`/ai?view=scheduled-tasks&taskId=${encodeURIComponent(newTaskId)}`)
      } else {
        navigate('/task-library?tab=my')
      }
    } catch (err: any) {
      toast.error(err?.error || err?.message || 'Could not install task')
    }
  }

  const integrations: string[] = Array.isArray(task?.integrations) ? task.integrations : []

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/50 tw-px-4 tw-py-8"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-px-6 tw-pt-5 tw-pb-4 tw-border-b tw-border-slate-100">
          <div className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-uppercase tw-tracking-wide tw-mb-1">
            Add to my tasks
          </div>
          <h2 className="tw-text-xl tw-font-semibold tw-text-slate-900 tw-leading-snug">
            {task?.name}
          </h2>
          {task?.summary && (
            <p className="tw-text-sm tw-text-slate-600 tw-mt-2 tw-leading-relaxed">
              {task.summary}
            </p>
          )}

          <div className="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
            <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-emerald-50 tw-px-3 tw-py-2">
              <DollarSign className="tw-h-4 tw-w-4 tw-text-emerald-600 tw-mt-0.5 tw-shrink-0" />
              <div className="tw-min-w-0">
                <div className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-emerald-700 tw-font-semibold">
                  Estimated savings
                </div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-truncate">
                  {formatSavings(task?.estimated_annual_savings_cents)}
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-slate-50 tw-px-3 tw-py-2">
              <Clock className="tw-h-4 tw-w-4 tw-text-slate-600 tw-mt-0.5 tw-shrink-0" />
              <div className="tw-min-w-0">
                <div className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-slate-500 tw-font-semibold">
                  Time to run
                </div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-truncate">
                  {formatRunTime(task?.estimated_run_seconds)}
                </div>
              </div>
            </div>
            <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-slate-50 tw-px-3 tw-py-2">
              <Plug className="tw-h-4 tw-w-4 tw-text-slate-600 tw-mt-0.5 tw-shrink-0" />
              <div className="tw-min-w-0">
                <div className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-slate-500 tw-font-semibold">
                  Integrations
                </div>
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-truncate">
                  {integrations.length > 0
                    ? integrations.map(formatIntegrationLabel).join(', ')
                    : 'None'}
                </div>
              </div>
            </div>
          </div>

          {task?.description && (
            <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-3">
              <div className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-slate-500 tw-font-semibold tw-mb-1">
                What this task does
              </div>
              <p className="tw-text-sm tw-text-slate-700 tw-leading-relaxed tw-whitespace-pre-wrap tw-m-0">
                {task.description}
              </p>
            </div>
          )}
        </div>
        <div className="tw-px-6 tw-py-5 tw-space-y-4">
          <TaskScheduleFields
            scheduleType={scheduleType}
            onScheduleTypeChange={setScheduleType}
            time={time}
            onTimeChange={setTime}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            dayOfWeek={dayOfWeek}
            onDayOfWeekChange={setDayOfWeek}
            dayOfMonth={dayOfMonth}
            onDayOfMonthChange={setDayOfMonth}
            allowOnce={false}
            showDateRange={false}
            previewLine={previewLine}
            compact
          />
          <TaskDeliveryFields
            notifyOnComplete={notifyOnComplete}
            onNotifyOnCompleteChange={(checked) => {
              setNotifyOnComplete(checked)
              if (checked && selectedNotifyEmails.length === 0 && userEmail) {
                setSelectedNotifyEmails([String(userEmail).trim().toLowerCase()])
              }
              if (!checked) setSelectedNotifyEmails([])
            }}
            selectedNotifyEmails={selectedNotifyEmails}
            onToggleNotifyEmail={toggleNotifyEmail}
            userEmail={userEmail}
            teamMembers={teamMembers}
            teamMembersLoading={!!teamMembersStatus}
          />
        </div>
        <div className="tw-px-6 tw-py-3 tw-border-t tw-border-slate-100 tw-flex tw-items-center tw-justify-end tw-gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={installing}
            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-text-slate-700 hover:tw-bg-slate-100 disabled:tw-opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInstall}
            disabled={installing}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-60"
          >
            {installing && <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />}
            Add to my tasks
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
